import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import type {
  ApiHomeResponse,
  ApiSettingsResponse,
  ApiProductsResponse,
  ApiProductDetailResponse,
  ApiCategoryProductsResponse,
  ApiCategory,
  ApiPage,
  ApiProduct,
} from '../types/api';

// ====== Homepage ======
export function useHomeData() {
  const [data, setData] = useState<ApiHomeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<ApiHomeResponse>('/home');
      setData(res.data);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ====== Site Settings (header/footer) ======
export function useSiteSettings() {
  const [data, setData] = useState<ApiSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<ApiSettingsResponse>('/settings')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

// ====== Products List ======
export function useProducts(params?: { category?: string; search?: string; featured?: boolean; page?: number }) {
  const [data, setData] = useState<ApiProductsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Create a stable key from params to trigger re-fetch
  const _category = params?.category || '';
  const _search = params?.search || '';
  const _featured = params?.featured || false;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const p: Record<string, any> = { page: 1, per_page: 50 };
    if (_category) p.category = _category;
    if (_search) p.search = _search;
    if (_featured) p.featured = '1';
    api.get<ApiProductsResponse>('/products', { params: p })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [_category, _search, _featured]);

  const goToPage = async (page: number) => {
    const p: Record<string, any> = { page, per_page: 50 };
    if (_category) p.category = _category;
    if (_search) p.search = _search;
    if (_featured) p.featured = '1';
    const res = await api.get<ApiProductsResponse>('/products', { params: p });
    setData(res.data);
  };

  return { data, loading, goToPage };
}

// ====== Product Detail ======
export function useProduct(slug: string) {
  const [data, setData] = useState<ApiProductDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get<ApiProductDetailResponse>(`/products/${slug}`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  return { data, loading };
}

// ====== Categories ======
export function useCategories() {
  const [data, setData] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<ApiCategory[]>('/categories')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

// ====== Category Products ======
export function useCategoryProducts(slug: string) {
  const [data, setData] = useState<ApiCategoryProductsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get<ApiCategoryProductsResponse>(`/categories/${slug}/products`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  return { data, loading };
}

// ====== Shoppable Videos ======
export function useShoppableVideos() {
  const [data, setData] = useState<ApiProduct[]>([]);

  useEffect(() => {
    api.get<ApiProduct[]>('/shoppable-videos')
      .then(res => setData(res.data))
      .catch(() => {});
  }, []);

  const incrementView = async (productId: number) => {
    await api.post(`/products/${productId}/video-view`).catch(() => {});
    setData(prev => prev.map(p =>
      p.id === productId ? { ...p, video_views: p.video_views + 1 } : p
    ));
  };

  return { data, incrementView };
}

// ====== Content Page ======
export function usePage(slug: string) {
  const [data, setData] = useState<ApiPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get<ApiPage>(`/pages/${slug}`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  return { data, loading };
}

// ====== Newsletter ======
export function subscribeNewsletter(email: string, name?: string, phone?: string) {
  return api.post('/newsletter', { email, full_name: name, phone });
}

// ====== Coupon Validation ======
export function validateCoupon(code: string) {
  return api.post('/coupon/validate', { code });
}

// ====== Auth ======
export function login(email: string, password: string) {
  return api.post('/login', { email, password });
}

export function register(name: string, email: string, password: string, phone?: string) {
  return api.post('/register', { name, email, password, phone });
}

// ====== Cart (API for logged-in users) ======
export function syncCartToServer(items: { product_id: number; quantity: number; price: number; image_id?: number; size?: string }[]) {
  return Promise.all(items.map(item => api.post('/cart/add', item)));
}

export function fetchCart() {
  return api.get('/cart');
}

export function removeCartItem(id: number) {
  return api.delete(`/cart/${id}`);
}

// ====== Wishlist (API for logged-in users) ======
export function toggleWishlistApi(productId: number) {
  return api.post('/wishlist/toggle', { product_id: productId });
}

export function fetchWishlist() {
  return api.get('/wishlist');
}

// ====== Orders ======
export function checkout(data: {
  items: { product_id: number; quantity: number; price: number; image_id?: number; size?: string }[];
  address_id?: number;
  method_paid?: string;
  shipping_cost?: number;
}) {
  return api.post('/checkout', data);
}

export function fetchOrders() {
  return api.get('/orders');
}
